/**
 * Asset Manifest — Journey Detail dataset list
 *
 * Replaces the prior train metaphor (Sprint 16, Epic F — DC-1521).
 * Same props, same behavior:
 *  - Header counters: datasets / stations / technical-asset score
 *  - Add Dataset CTA
 *  - Per-asset cards with type, status, health, and governance indicators
 *  - Click → AssetDetailPanel (data-owner / data-steward role assignment lives there)
 *  - Hover tooltip with full asset metadata
 *  - Governance status fetched from /api/governance/journeys/:id/assets/batch
 *
 * Exported as `TrainVisualization` for import-site stability; the train
 * styling is gone. File rename can land as a follow-up sweep.
 */

import React, { useState, useEffect } from 'react';
import {
  Database,
  FileText,
  Mic,
  Plus,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  type LucideIcon,
} from 'lucide-react';
import { useJourneyId } from '../contexts/JourneyContext';
import '../styles/journey.css';

// ============================================================================
// TYPES
// ============================================================================

interface Asset {
  asset_id: string;
  type: 'dataset' | 'interview' | 'document';
  name: string;
  location: string;
  created_at: string;
  health_score?: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
}

type GovernanceStatus = 'complete' | 'partial' | 'missing';

interface AssetGovernanceInfo {
  governance_status: GovernanceStatus;
  assigned_count: number;
  baseline_assigned: number;
  baseline_total: number;
}

interface TrainVisualizationProps {
  groupId?: string;
  assets: Asset[];
  /** When omitted, the Add Dataset affordances are hidden (assets fixed at journey creation). */
  onAddAssets?: () => void;
  onAssetClick?: (asset: Asset) => void;
  currentStationIndex?: number;
  refreshTrigger?: number;
  technicalAssetScore?: number | null;
}

// ============================================================================
// VISUAL CONFIG
// ============================================================================

const ASSET_CONFIG: Record<
  Asset['type'],
  { Icon: LucideIcon; label: string; accent: string }
> = {
  dataset: { Icon: Database, label: 'Dataset', accent: '#3b82f6' },
  interview: { Icon: Mic, label: 'Interview', accent: '#a855f7' },
  document: { Icon: FileText, label: 'Document', accent: '#10b981' },
};

const GOVERNANCE_CONFIG: Record<
  GovernanceStatus,
  { Icon: LucideIcon; label: string; color: string }
> = {
  complete: { Icon: ShieldCheck, label: 'Governance complete', color: '#10b981' },
  partial: { Icon: ShieldAlert, label: 'Governance partial', color: '#f59e0b' },
  missing: { Icon: ShieldX, label: 'No governance roles assigned', color: '#ef4444' },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const TrainVisualization: React.FC<TrainVisualizationProps> = ({
  groupId: groupIdProp,
  assets,
  onAddAssets,
  onAssetClick,
  refreshTrigger = 0,
  technicalAssetScore = null,
}) => {
  const groupId = useJourneyId(groupIdProp);
  const [hoveredAssetId, setHoveredAssetId] = useState<string | null>(null);
  const [governanceStatus, setGovernanceStatus] = useState<Record<string, AssetGovernanceInfo>>({});
  const [_governanceLoading, setGovernanceLoading] = useState(false);

  const datasetAssets = assets.filter((a) => a.type === 'dataset');
  const datasetCount = datasetAssets.length;

  useEffect(() => {
    const fetchGovernanceStatus = async () => {
      if (datasetAssets.length === 0) {
        setGovernanceStatus({});
        return;
      }

      setGovernanceLoading(true);
      try {
        const assetIds = datasetAssets.map((a) => a.asset_id).join(',');
        const response = await fetch(
          `/api/governance/journeys/${groupId}/assets/batch?asset_ids=${assetIds}`,
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.assets) {
            setGovernanceStatus(data.assets);
          }
        }
      } catch (err) {
        console.error('[TrainVisualization] Failed to fetch governance status:', err);
      } finally {
        setGovernanceLoading(false);
      }
    };

    fetchGovernanceStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when asset count changes
  }, [groupId, datasetAssets.length, refreshTrigger]);

  const getHealthColor = (score?: number): string => {
    if (!score) return '#94a3b8';
    if (score >= 4) return '#10b981';
    if (score >= 3) return '#f59e0b';
    return '#ef4444';
  };

  const getHealthLabel = (score?: number): string => {
    if (!score) return 'Unknown';
    if (score >= 4) return 'Excellent';
    if (score >= 3) return 'Good';
    return 'Needs Work';
  };

  const getStatusIndicator = (
    status: Asset['status'],
  ): { Icon: LucideIcon; color: string; label: string; spinning?: boolean } => {
    switch (status) {
      case 'pending':
        return { Icon: Clock, color: '#94a3b8', label: 'Pending' };
      case 'processing':
        return { Icon: Loader2, color: '#3b82f6', label: 'Processing', spinning: true };
      case 'ready':
        return { Icon: CheckCircle2, color: '#10b981', label: 'Ready' };
      case 'error':
        return { Icon: XCircle, color: '#ef4444', label: 'Error' };
    }
  };

  const getGovernanceIndicator = (
    asset: Asset,
  ): { Icon: LucideIcon; color: string; tooltip: string; label: string } | null => {
    if (asset.type !== 'dataset') return null;
    const info = governanceStatus[asset.asset_id];
    if (!info) {
      const cfg = GOVERNANCE_CONFIG.missing;
      return { Icon: cfg.Icon, color: cfg.color, tooltip: cfg.label, label: cfg.label };
    }
    const cfg = GOVERNANCE_CONFIG[info.governance_status];
    return {
      Icon: cfg.Icon,
      color: cfg.color,
      tooltip: `${cfg.label} (${info.baseline_assigned}/${info.baseline_total} baseline roles)`,
      label: cfg.label,
    };
  };

  const handleAssetClick = (asset: Asset) => {
    if (onAssetClick) onAssetClick(asset);
  };

  const getTechnicalScoreClass = (score: number): string => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-fair';
    return 'score-poor';
  };

  return (
    <div className="asset-manifest">
      {/* Header — counters + Add Dataset CTA */}
      <div className="asset-manifest-header">
        <div className="asset-manifest-stats">
          <div className="manifest-stat">
            <span className="stat-label">Datasets</span>
            <span className="stat-value">{datasetCount}</span>
          </div>
          {/* v2.5 SS-02/B2: the Stations N/M tile is GONE — it was a second
              completeness denominator outside the canonical status contract
              (and defaulted to a fabricated /4). The status panel is the only
              completeness surface. */}
          {technicalAssetScore !== null && (
            <div className="manifest-stat">
              <span className="stat-label">Technical Asset</span>
              <span className={`stat-value ${getTechnicalScoreClass(technicalAssetScore)}`}>
                {technicalAssetScore}%
              </span>
            </div>
          )}
        </div>
        {onAddAssets && (
          <button
            type="button"
            className="btn btn-secondary btn-sm asset-manifest-add"
            onClick={onAddAssets}
          >
            <Plus size={16} aria-hidden="true" />
            <span>Add Dataset</span>
          </button>
        )}
      </div>

      {/* Asset list */}
      {assets.length === 0 ? (
        <div className="asset-manifest-empty">
          <Database size={32} aria-hidden="true" className="empty-icon" />
          <p className="empty-text">No datasets in this journey yet.</p>
          {onAddAssets && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onAddAssets}>
              <Plus size={16} aria-hidden="true" />
              <span>Add Your First Dataset</span>
            </button>
          )}
        </div>
      ) : (
        <div className="asset-grid" role="list">
          {assets.map((asset) => {
            const config = ASSET_CONFIG[asset.type];
            const TypeIcon = config.Icon;
            const status = getStatusIndicator(asset.status);
            const StatusIcon = status.Icon;
            const governance = getGovernanceIndicator(asset);
            const GovIcon = governance?.Icon;
            const isHovered = hoveredAssetId === asset.asset_id;
            const isClickable = Boolean(onAssetClick);

            return (
              <div
                key={asset.asset_id}
                role="listitem"
                className={`asset-card ${isClickable ? 'asset-card-clickable' : ''} ${
                  isHovered ? 'asset-card-hovered' : ''
                }`}
                onMouseEnter={() => setHoveredAssetId(asset.asset_id)}
                onMouseLeave={() => setHoveredAssetId(null)}
                onClick={() => handleAssetClick(asset)}
                onKeyDown={(e) => {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleAssetClick(asset);
                  }
                }}
                tabIndex={isClickable ? 0 : -1}
                style={{ borderTopColor: config.accent }}
              >
                <div className="asset-card-row">
                  <div className="asset-card-type">
                    <TypeIcon size={14} aria-hidden="true" style={{ color: config.accent }} />
                    <span>{config.label}</span>
                  </div>
                  {governance && GovIcon && (
                    <div
                      className="asset-card-governance"
                      title={governance.tooltip}
                      aria-label={governance.tooltip}
                    >
                      <GovIcon size={14} aria-hidden="true" style={{ color: governance.color }} />
                    </div>
                  )}
                </div>

                <h4 className="asset-card-name" title={asset.name}>
                  {asset.name}
                </h4>

                <div className="asset-card-row asset-card-footer">
                  <div className="asset-card-status" style={{ color: status.color }}>
                    <StatusIcon
                      size={14}
                      aria-hidden="true"
                      className={status.spinning ? 'spin' : ''}
                    />
                    <span>{status.label}</span>
                  </div>
                  {asset.health_score !== undefined && (
                    <span
                      className="asset-card-health"
                      style={{ background: getHealthColor(asset.health_score) }}
                      title={`Health: ${asset.health_score.toFixed(1)}/5 (${getHealthLabel(asset.health_score)})`}
                    >
                      {asset.health_score.toFixed(1)}/5
                    </span>
                  )}
                </div>

                {isHovered && (
                  <div className="asset-card-tooltip" role="tooltip">
                    <div className="tooltip-name">{asset.name}</div>
                    <dl className="tooltip-meta">
                      <div>
                        <dt>Type</dt>
                        <dd>{config.label}</dd>
                      </div>
                      <div>
                        <dt>Status</dt>
                        <dd>{status.label}</dd>
                      </div>
                      {asset.health_score !== undefined && (
                        <div>
                          <dt>Health</dt>
                          <dd>
                            {asset.health_score.toFixed(1)}/5 · {getHealthLabel(asset.health_score)}
                          </dd>
                        </div>
                      )}
                      {governance && (
                        <div>
                          <dt>Governance</dt>
                          <dd>{governance.tooltip}</dd>
                        </div>
                      )}
                      <div>
                        <dt>Uploaded</dt>
                        <dd>{new Date(asset.created_at).toLocaleDateString()}</dd>
                      </div>
                    </dl>
                    {isClickable && (
                      <div className="tooltip-hint">
                        Click to view details and assign data-governance roles
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrainVisualization;
